import { useMemo, useEffect, memo, useRef, useState } from "react";
import * as THREE from "three";
import { Text } from "@react-three/drei";
import { createBuildingGeometry } from "../../objectSystem/buildingFactory";
import { getBuildingStructure } from "../../config/buildingPresets";
import { BUILDING_CONFIG } from "../../config/constants";
import {
  calculateBoundingBox,
  createBuildingEdgesGeometry,
  calculateBoxFaceAABB,
} from "../../utils/buildingGeometry";
import { createBuildingShaderMaterial } from "../../materials/buildingShaderMaterial";
import type { BuildingStructureBox } from "@/utils/supabase";
import type { TenantInstance } from "@/three/config/types";
import useSound from "use-sound";

interface BuildingModelProps {
  id: string;
  position: [number, number, number];
  meshIndex: number;
  buildingStructure?: BuildingStructureBox[] | null;
  title?: string | null;
  color?: THREE.ColorRepresentation;
  isSelected?: boolean;
  /** 엣지 렌더링 여부 (기본값: true, MergedBuildingEdges 사용 시 false) */
  renderEdges?: boolean;
  /** 호버 상태 - 호버 시 건물이 위로 올라감 */
  isHovered?: boolean;
  /** 사용자 컬러 (엣지 색상에 사용) */
  userColor?: string | null;
  /** 입주자 목록 */
  tenants?: TenantInstance[];
  /** 투명도 (0~1, 기본값 1) */
  opacity?: number;
  /** CSG 음각 모드 사용 여부 (true: 진짜 3D 음각, false: 레이어 방식) */
  useCSGEngraving?: boolean;
  /** 텍스트 숨김 (GUI 클리핑 영역에 있을 때) */
  hideText?: boolean;
}

/**
 * 입주자 텍스트 컴포넌트 (성능 최적화를 위해 메모이제이션)
 */
const TenantText = memo(function TenantText({
  tenant,
  boundingBox,
  structure,
  userColor,
  opacity = 1,
}: {
  tenant: TenantInstance;
  boundingBox: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    minZ: number;
    maxZ: number;
  };
  structure: Array<{
    position: [number, number, number];
    scale: [number, number, number];
  }>;
  userColor?: string | null;
  opacity?: number;
}) {
  // 바운딩 박스에서 각 축별 크기 계산
  const widthX = boundingBox.maxX - boundingBox.minX;
  const widthZ = boundingBox.maxZ - boundingBox.minZ;
  const buildingHeight = boundingBox.maxY - boundingBox.minY;

  // positionU, positionV (0~1) → 실제 좌표
  const u = tenant.positionU;
  const v = tenant.positionV;

  // 투영 축에 따른 위치 및 회전 계산
  const isNegativeAxis =
    tenant.projectionAxis === "-x" || tenant.projectionAxis === "-z";
  const normalizedAxis =
    tenant.projectionAxis === "-x"
      ? "x"
      : tenant.projectionAxis === "-z"
      ? "z"
      : tenant.projectionAxis;
  const normalizedU = isNegativeAxis ? 1 - u : u;

  // 실제 Y 위치 계산
  const actualY = boundingBox.minY + v * buildingHeight;

  // 해당 위치에서 실제 벽면 찾기
  let wallPosition: number;
  let textPosition: [number, number, number];
  let textRotation: [number, number, number];
  const offset = 0.05;

  if (normalizedAxis === "x") {
    // X+ 방향: 해당 Z, Y 위치에서 가장 오른쪽 벽 찾기
    const targetZ = boundingBox.minZ + normalizedU * widthZ;

    // 해당 Z, Y 위치를 포함하는 박스들 중 가장 큰 maxX 찾기
    let maxXAtPosition = boundingBox.minX; // 기본값
    for (const box of structure) {
      const [px, py, pz] = box.position;
      const [sx, sy, sz] = box.scale;
      const boxMinZ = pz - sz / 2;
      const boxMaxZ = pz + sz / 2;
      const boxMinY = py - sy / 2;
      const boxMaxY = py + sy / 2;
      const boxMaxX = px + sx / 2;

      // 이 박스가 targetZ와 actualY를 포함하는지 확인
      if (
        targetZ >= boxMinZ &&
        targetZ <= boxMaxZ &&
        actualY >= boxMinY &&
        actualY <= boxMaxY
      ) {
        maxXAtPosition = Math.max(maxXAtPosition, boxMaxX);
      }
    }

    wallPosition = maxXAtPosition;
    textPosition = [wallPosition + offset, actualY, targetZ];
    textRotation = [0, Math.PI / 2, 0];
  } else {
    // Z+ 방향: 해당 X, Y 위치에서 가장 앞쪽 벽 찾기
    const targetX = boundingBox.minX + normalizedU * widthX;

    // 해당 X, Y 위치를 포함하는 박스들 중 가장 큰 maxZ 찾기
    let maxZAtPosition = boundingBox.minZ; // 기본값
    for (const box of structure) {
      const [px, py, pz] = box.position;
      const [sx, sy, sz] = box.scale;
      const boxMinX = px - sx / 2;
      const boxMaxX = px + sx / 2;
      const boxMinY = py - sy / 2;
      const boxMaxY = py + sy / 2;
      const boxMaxZ = pz + sz / 2;

      // 이 박스가 targetX와 actualY를 포함하는지 확인
      if (
        targetX >= boxMinX &&
        targetX <= boxMaxX &&
        actualY >= boxMinY &&
        actualY <= boxMaxY
      ) {
        maxZAtPosition = Math.max(maxZAtPosition, boxMaxZ);
      }
    }

    wallPosition = maxZAtPosition;
    textPosition = [targetX, actualY, wallPosition + offset];
    textRotation = [0, 0, 0];
  }

  // 세로 텍스트 폰트 크기 (건물 높이 기준, 최소 크기 보장)
  const fontSize = Math.max(0.15, Math.min(0.4, buildingHeight * 0.1));

  // X축일 때는 텍스트가 90도 회전되므로 anchor 조정
  const anchorX = normalizedAxis === "x" ? "center" : "left";

  // 3D 음각(새김) 효과를 위한 깊이 설정
  const insetDepth = 0.04; // 함몰 깊이
  const layerCount = 3; // 깊이 레이어 수

  // 함몰 방향 (벽면 안쪽 방향 - 음각)
  const insetDirection: [number, number, number] =
    normalizedAxis === "x"
      ? [-insetDepth, 0, 0] // X축 벽면은 -X 방향으로 함몰
      : [0, 0, -insetDepth]; // Z축 벽면은 -Z 방향으로 함몰

  // 건물 색상 기반 음각 색상 계산 (자연스러운 새김 효과)
  const baseColor = userColor
    ? new THREE.Color(userColor)
    : new THREE.Color("#333333");
  const deepColor = baseColor.clone().multiplyScalar(0.2); // 가장 깊은 곳: 20% 밝기
  const getLayerColor = (ratio: number) =>
    baseColor.clone().multiplyScalar(0.3 + ratio * 0.3); // 30%~60%
  const surfaceColor = baseColor.clone().multiplyScalar(0.8); // 표면: 80% 밝기

  return (
    <group>
      {/* 가장 깊은 레이어 (바닥 - 건물 색상의 어두운 버전) */}
      <Text
        position={[
          textPosition[0] + insetDirection[0],
          textPosition[1],
          textPosition[2] + insetDirection[2],
        ]}
        rotation={textRotation}
        fontSize={fontSize}
        color={`#${deepColor.getHexString()}`}
        anchorX={anchorX}
        anchorY="bottom"
        maxWidth={buildingHeight * 0.8}
        textAlign="left"
        renderOrder={996}
        fillOpacity={opacity}
        material={
          new THREE.MeshBasicMaterial({
            color: deepColor,
            depthTest: true,
            depthWrite: true,
            transparent: true,
            opacity: opacity,
          })
        }
      >
        {tenant.text.split("").join("\n")}
      </Text>

      {/* 중간 레이어들 (깊이감 표현 - 건물 색상 기반으로 점점 밝아짐) */}
      {Array.from({ length: layerCount - 1 }).map((_, i) => {
        const depthRatio = (i + 1) / layerCount; // 0.33, 0.67
        const layerPosition: [number, number, number] = [
          textPosition[0] + insetDirection[0] * (1 - depthRatio),
          textPosition[1],
          textPosition[2] + insetDirection[2] * (1 - depthRatio),
        ];
        // 표면에 가까워질수록 건물 색상에 가까워짐
        const layerColor = getLayerColor(depthRatio);

        return (
          <Text
            key={`depth-${i}`}
            position={layerPosition}
            rotation={textRotation}
            fontSize={fontSize}
            color={`#${layerColor.getHexString()}`}
            anchorX={anchorX}
            anchorY="bottom"
            maxWidth={buildingHeight * 0.8}
            textAlign="left"
            renderOrder={997 + i}
            fillOpacity={opacity}
            material={
              new THREE.MeshBasicMaterial({
                color: layerColor,
                depthTest: true,
                depthWrite: true,
                transparent: true,
                opacity: opacity,
              })
            }
          >
            {tenant.text.split("").join("\n")}
          </Text>
        );
      })}

      {/* 표면 레이어 (하이라이트 가장자리 - 건물 색상 기반) */}
      <Text
        position={textPosition}
        rotation={textRotation}
        fontSize={fontSize}
        color={`#${surfaceColor.getHexString()}`}
        anchorX={anchorX}
        anchorY="bottom"
        maxWidth={buildingHeight * 0.8}
        textAlign="left"
        renderOrder={1000}
        fillOpacity={opacity * 0.7}
        outlineWidth={0.003}
        outlineColor={`#${deepColor.getHexString()}`}
        material={
          new THREE.MeshBasicMaterial({
            color: surfaceColor,
            depthTest: true,
            depthWrite: true,
            transparent: true,
            opacity: opacity * 0.7,
          })
        }
      >
        {tenant.text.split("").join("\n")}
      </Text>
    </group>
  );
});

/**
 * CSG 기반 건물 모델 컴포넌트
 * - 흰색 건물에 검은 엣지 라인
 * - 입주자 텍스트를 건물 표면에 표시
 */
export const BuildingModel = memo(function BuildingModel({
  id, // eslint-disable-line @typescript-eslint/no-unused-vars
  position,
  meshIndex,
  buildingStructure,
  title,
  color = BUILDING_CONFIG.DEFAULT_COLOR,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isSelected = false,
  renderEdges = true, // 기본값: true (개별 렌더링)
  isHovered = false, // 호버 상태
  userColor = null, // 사용자 컬러
  opacity = 1.0, // 투명도
}: BuildingModelProps) {
  const groupRef = useRef<THREE.Group>(null);

  // 호버 사운드
  const [playBuildingUp] = useSound(
    import.meta.env.BASE_URL + "/sound/building_up.mp3",
    { volume: 0.2 }
  );
  const prevHoveredRef = useRef(false);

  // 건물 구조 가져오기
  const structure = useMemo(() => {
    return buildingStructure?.length
      ? buildingStructure
      : getBuildingStructure(meshIndex);
  }, [meshIndex, buildingStructure]);

  // CSG 지오메트리 생성 (캐시 활용) - CSG 음각 모드가 아니거나 아직 로드 중일 때 사용
  const baseGeometry = useMemo(() => {
    return createBuildingGeometry(meshIndex, buildingStructure);
  }, [meshIndex, buildingStructure]);

  // 최종 지오메트리 선택
  const geometry = baseGeometry;

  // 건물용 커스텀 셰이더 머티리얼
  // - 면 방향 기반 고정 밝기 (아이소메트릭 최적화)
  // - 지붕: 노이즈 효과
  // - 벽면: 수직 그라데이션 (vertical/horizontal/stripes 전환 가능)
  const buildingMaterial = useMemo(() => {
    // 유저 컬러가 없으면 기본 색상 사용
    const colorHex =
      userColor || (typeof color === "string" ? color : "#ffffff");

    // 건물 ID를 해시해서 일관된 시드 생성 (0~1000 범위)
    let seed = 0;
    for (let i = 0; i < id.length; i++) {
      seed = ((seed << 5) - seed + id.charCodeAt(i)) | 0;
    }
    seed = Math.abs(seed) % 1000;

    return createBuildingShaderMaterial({
      userColor: colorHex,
      noiseIntensity: 0.15, // 지붕 노이즈 강도
      wallPattern: "vertical", // 수직 그라데이션
      patternIntensity: 0.4, // 벽면 패턴 강도 (강화)
      opacity: opacity,
      doubleSide: true, // GUI 클리핑 시 backface가 보이도록
      buildingSeed: seed,
    });
  }, [id, userColor, color, opacity]);

  // 모든 상단면 박스 찾기 (같은 최대 Y 높이를 가진 박스들)
  const topFaces = useMemo(() => {
    if (!structure.length) return [];

    // 최대 Y 높이 찾기
    let maxY = -Infinity;
    structure.forEach((box) => {
      const [, py] = box.position;
      const [, sy] = box.scale;
      const boxMaxY = py + sy / 2;
      if (boxMaxY > maxY) {
        maxY = boxMaxY;
      }
    });

    // 같은 최대 Y 높이를 가진 박스들 찾기
    const topBoxes = structure.filter((box) => {
      const [, py] = box.position;
      const [, sy] = box.scale;
      const boxMaxY = py + sy / 2;
      return Math.abs(boxMaxY - maxY) < 0.001; // epsilon 비교
    });

    // X축 방향으로 정렬 (왼쪽에서 오른쪽으로)
    return topBoxes
      .map((box) => calculateBoxFaceAABB(box, "y"))
      .sort((a, b) => a.minX - b.minX); // X축 기준 정렬
  }, [structure]);

  // 텍스트를 상단면들에 분배 (줄바꿈 유지, 세로 공간 부족 시 다음 면으로)
  const titleSegments = useMemo(() => {
    if (!title || topFaces.length === 0) return [];

    // Canvas를 사용하여 텍스트 크기 측정
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return [];

    const lineHeight = 1.2;

    const segments: Array<{
      text: string;
      aabb: (typeof topFaces)[0];
    }> = [];

    let remainingText = title;
    let currentFaceIndex = 0;

    while (remainingText.length > 0 && currentFaceIndex < topFaces.length) {
      const face = topFaces[currentFaceIndex];
      const maxWidth = face.maxX - face.minX;
      const maxHeight = face.maxZ - face.minZ; // Z축이 세로 방향
      const fontSize = (face.maxZ - face.minZ) * 0.2;

      ctx.font = `bold ${fontSize}px sans-serif`;

      // 줄바꿈을 고려하여 텍스트 분할
      const words = remainingText.split("");
      const lines: string[] = [];
      let currentLine = "";
      let totalHeight = 0;
      const lineHeightPx = fontSize * lineHeight;

      for (let i = 0; i < words.length; i++) {
        const char = words[i];
        const testLine = currentLine + char;
        const metrics = ctx.measureText(testLine);

        if (metrics.width > maxWidth && currentLine !== "") {
          // 줄바꿈
          lines.push(currentLine);
          totalHeight += lineHeightPx;

          // 높이가 면의 높이를 넘어가면 이 면에서 중단
          if (totalHeight + lineHeightPx > maxHeight) {
            break;
          }

          currentLine = char;
        } else {
          currentLine = testLine;
        }
      }

      // 마지막 줄 추가 (높이 체크)
      if (currentLine && totalHeight + lineHeightPx <= maxHeight) {
        lines.push(currentLine);
        totalHeight += lineHeightPx;
      }

      if (lines.length > 0) {
        const textForThisFace = lines.join("\n");
        segments.push({
          text: textForThisFace,
          aabb: face,
        });

        // 사용한 텍스트 길이 계산
        const usedLength = textForThisFace.replace(/\n/g, "").length;
        remainingText = remainingText.slice(usedLength);
      }

      currentFaceIndex++;
    }

    return segments;
  }, [title, topFaces]);

  // 박스 구조 기반 외곽선 생성
  const edgesGeometry = useMemo(() => {
    return createBuildingEdgesGeometry(structure);
  }, [structure]);

  // 엣지 라인 머티리얼 (유저 컬러 사용, 채도와 명도 낮춤)
  const edgeMaterial = useMemo(() => {
    if (userColor) {
      // hex string -> RGB 변환
      const hex = userColor.replace("#", "");
      let r = parseInt(hex.substring(0, 2), 16) / 255;
      let g = parseInt(hex.substring(2, 4), 16) / 255;
      let b = parseInt(hex.substring(4, 6), 16) / 255;

      // 채도 낮추기: 회색과 섞기 (50% 회색, 50% 원본 색상)
      const grayMix = 0.5;
      const avg = (r + g + b) / 3;
      r = r * (1 - grayMix) + avg * grayMix;
      g = g * (1 - grayMix) + avg * grayMix;
      b = b * (1 - grayMix) + avg * grayMix;

      // 명도 낮추기: 전체적으로 어둡게 (0.65 배율)
      const brightness = 0.65;
      r *= brightness;
      g *= brightness;
      b *= brightness;

      return new THREE.LineBasicMaterial({
        color: new THREE.Color(r, g, b),
        linewidth: 1,
      });
    } else {
      // 기본 엣지 컬러 사용
      return new THREE.LineBasicMaterial({
        color: BUILDING_CONFIG.EDGE_COLOR,
        linewidth: 1,
      });
    }
  }, [userColor]);

  // 호버 시 건물 높이 올리기 및 사운드 재생
  useEffect(() => {
    if (!groupRef.current) return;

    groupRef.current.position.y = isHovered ? position[1] + 0.3 : position[1];

    // 호버 상태가 false에서 true로 변경될 때만 사운드 재생
    if (isHovered && !prevHoveredRef.current) {
      playBuildingUp();
    }
    prevHoveredRef.current = isHovered;
  }, [isHovered, position, playBuildingUp]);

  return (
    <group ref={groupRef} position={position}>
      {/* 메인 건물 메시 */}
      <mesh geometry={geometry} material={buildingMaterial} />

      {/* 상단면들에 title 표시 (troika-text 사용, 여러 면에 분배) */}
      {titleSegments.map((segment, index) => {
        const fontSize = (segment.aabb.maxZ - segment.aabb.minZ) * 0.2;
        return (
          <Text
            key={index}
            position={[
              segment.aabb.minX, // 왼쪽 모서리
              segment.aabb.maxY + 0.01, // 상단면 위
              segment.aabb.minZ, // 앞쪽 모서리
            ]}
            rotation={[-Math.PI / 2, 0, 0]} // 수평 평면에 배치
            fontSize={fontSize}
            maxWidth={segment.aabb.maxX - segment.aabb.minX} // 상단면 너비(X축)를 최대 너비로
            color={BUILDING_CONFIG.TEXT_COLOR as string}
            anchorX="left"
            anchorY="top"
            textAlign="left"
            lineHeight={1.2}
            overflowWrap="break-word"
            fillOpacity={opacity}
          >
            {segment.text}
          </Text>
        );
      })}

      {renderEdges && (
        <lineSegments geometry={edgesGeometry} material={edgeMaterial} />
      )}
    </group>
  );
});
